import React, { useState, useEffect } from 'react';
import { AccessDeniedPage } from './components/RouteStates';
import {
  Users, Mail, Shield, MapPin, Building2, Plus, MoreVertical,
  Trash2, X, Check, Search, AlertCircle, Loader
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Reusable components (Spinner, Modal)
const Spinner = () => (
  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
);

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

export const MembershipPage = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [forbidden, setForbidden] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [orgId, setOrgId] = useState(null);
  
  // Invite Form State
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoles, setInviteRoles] = useState(['staff']);
  const [inviteDepts, setInviteDepts] = useState([]);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    init();
  }, []);

  const init = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) return;

      // 1. Get Context (Organization)
      const ctxRes = await fetch(`${API_URL}/api/platform/context`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const ctx = await ctxRes.json();
      const oid = ctx.organizations?.[0]?.id;
      
      if (!oid) {
        setError("No organization found. Please contact support.");
        return;
      }
      setOrgId(oid);

      // 2. Fetch Members
      await fetchMembers(token, oid);

    } catch (err) {
      console.error(err);
      setError("Failed to load membership data.");
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async (token, oid) => {
    const res = await fetch(`${API_URL}/api/platform/memberships?organization_id=${oid}`, {
        headers: { 
            Authorization: `Bearer ${token}`,
            'X-Organization-Id': oid
        }
    });
    // A 403 here is the access-control system working, not a failure. Render
    // it as an explicit permission state (D4) instead of the generic
    // "Failed to load membership data", which made a correct denial look like
    // an outage.
    if (res.status === 403) {
      setForbidden(true);
      return;
    }
    if (!res.ok) throw new Error("Failed to list members");
    const data = await res.json();
    setMembers(data);
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    
    try {
        setInviting(true);
        const token = localStorage.getItem('token');
        
        const res = await fetch(`${API_URL}/api/platform/memberships/invite`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Organization-Id': orgId
            },
            body: JSON.stringify({
                organization_id: orgId,
                email: inviteEmail,
                roles: inviteRoles,
                departments: inviteDepts,
                location_ids: [] // All locations for now
            })
        });
        
        if (!res.ok) {
            const err = await res.json();
            throw new Error(err.detail || "Invite failed");
        }

        setShowInvite(false);
        setInviteEmail('');
        alert('Invite sent successfully!');
        await fetchMembers(token, orgId); // Refresh list
        
    } catch (err) {
        alert(err.message);
    } finally {
        setInviting(false);
    }
  };

  const removeMember = async (memberId) => {
      if (!window.confirm("Are you sure you want to remove this member?")) return;
      
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/platform/memberships/${memberId}`, {
            method: 'DELETE',
            headers: {
                Authorization: `Bearer ${token}`,
                'X-Organization-Id': orgId
            }
        });
        
        if (!res.ok) {
             const err = await res.json();
            throw new Error(err.detail || "Remove failed");
        }
        
        await fetchMembers(token, orgId);
      } catch (err) {
          alert(err.message);
      }
  };

  if (forbidden) {
    return (
      <AccessDeniedPage detail="Managing team members requires an owner or admin role in this organization." />
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Team Members</h1>
          <p className="text-gray-500 mt-1">Manage access and roles for your organization</p>
        </div>
        <button 
            onClick={() => setShowInvite(true)}
            className="bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-600 transition flex items-center gap-2"
        >
            <Plus className="w-4 h-4" /> Invite Member
        </button>
      </div>

      {loading ? (
          <div className="flex justify-center py-12"><Spinner /></div>
      ) : error ? (
          <div className="bg-red-50 text-red-600 p-4 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" /> {error}
          </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Roles</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Access</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {members.map((m) => (
                        <tr key={m.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-bold">
                                        {(m.user?.name?.[0] || m.user?.email?.[0] || '?').toUpperCase()}
                                    </div>
                                    <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">{m.user?.name || 'Unknown'}</div>
                                        <div className="text-sm text-gray-500">{m.user?.email}</div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex gap-1 flex-wrap">
                                    {m.roles.map(r => (
                                        <span key={r} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
                                            {r.replace('_', ' ')}
                                        </span>
                                    ))}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                {m.location_ids?.length ? `${m.location_ids.length} Locations` : 'All Locations'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Active
                                </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                <button onClick={() => removeMember(m.id)} className="text-red-600 hover:text-red-900">
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
      )}

      {/* Invite Modal */}
      <Modal isOpen={showInvite} onClose={() => setShowInvite(false)} title="Invite Team Member">
          <form onSubmit={handleInvite} className="space-y-4">
              <div>
                  <label className="block text-sm font-medium text-gray-700">Email Address</label>
                  <input aria-label="Email Address" 
                    type="email" 
                    required
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm p-2 border"
                    placeholder="colleague@restaurant.com"
                  />
              </div>
              
              <div>
                  <label className="block text-sm font-medium text-gray-700">Role</label>
                  <select aria-label="Role" 
                    value={inviteRoles[0]}
                    onChange={e => setInviteRoles([e.target.value])}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm p-2 border"
                  >
                      <option value="staff">Staff</option>
                      <option value="manager">Manager</option>
                      <option value="org_admin">Admin</option>
                  </select>
              </div>

              <div className="pt-4 flex justify-end gap-3">
                  <button type="button" onClick={() => setShowInvite(false)} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-md border">
                      Cancel
                  </button>
                  <button type="submit" disabled={inviting} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md disabled:opacity-50">
                      {inviting ? 'Sending...' : 'Send Invite'}
                  </button>
              </div>
          </form>
      </Modal>
    </div>
  );
};
